import { useRef, useState } from "react";

interface StlDropzoneProps {
  onFile: (file: File) => void;
  disabled?: boolean;
}

export const MAX_STL_BYTES = 100 * 1024 * 1024;

/**
 * Desktop/Android picker hint. Includes the extension plus MIME aliases
 * STL exporters actually emit. Do not use `model/stl` alone — iOS Files
 * has no UTI for it and greys out / hides `.stl` files.
 */
export const STL_ACCEPT_HINTS =
  ".stl,application/sla,model/stl,application/vnd.ms-pki.stl";

/**
 * iOS Safari / Files: document-oriented accept so the picker opens
 * Files/Browse instead of Camera / Photo Library, while still leaving
 * unrecognised `.stl` tappable. Do not use the all-types wildcard
 * (`*` + `/*`) or omit `accept` (both show the media action sheet).
 * Do not add `capture`.
 */
export const IOS_ACCEPT_FALLBACK = "application/octet-stream,.stl";

export function isIosFilePicker(userAgent: string, maxTouchPoints = 0): boolean {
  if (/iPad|iPhone|iPod/i.test(userAgent)) return true;
  // iPadOS 13+ Safari reports as Macintosh + touch.
  return /Macintosh/i.test(userAgent) && maxTouchPoints > 1;
}

export function stlInputAccept(
  userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent,
  maxTouchPoints = typeof navigator === "undefined" ? 0 : navigator.maxTouchPoints,
): string {
  if (isIosFilePicker(userAgent, maxTouchPoints)) return IOS_ACCEPT_FALLBACK;
  return STL_ACCEPT_HINTS;
}

/** JS gate: name must end with .stl. STEP / OBJ / 3MF stay rejected. */
export function isStlFilename(name: string): boolean {
  return name.toLowerCase().endsWith(".stl");
}

export function StlDropzone({ onFile, disabled }: StlDropzoneProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handle(file: File): void {
    if (!isStlFilename(file.name)) {
      setError("Only .stl files are supported.");
      return;
    }
    if (file.size > MAX_STL_BYTES) {
      setError("File is over 100 MB.");
      return;
    }
    setError(null);
    onFile(file);
  }

  return (
    <div className="flex w-full flex-col gap-2">
      <label
        data-testid="stl-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file) handle(file);
        }}
        className={[
          "flex h-48 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed text-center transition-colors",
          dragOver ? "border-primary bg-accent" : "border-border bg-bg",
          disabled ? "pointer-events-none opacity-50" : "",
        ].join(" ")}
      >
        <input
          ref={inputRef}
          type="file"
          accept={stlInputAccept()}
          className="sr-only"
          data-testid="stl-input"
          disabled={disabled}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handle(file);
          }}
        />
        <span className="text-base font-medium">Drop your STL here</span>
        <span className="mt-1 text-sm text-muted-fg">or click to browse</span>
      </label>
      {error && (
        <p className="text-sm text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
